import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { GenericHeightWeightQuestionFieldAnswers } from '../../Generic/HeightWeight/question';

const peptideQuestions = [
  {
    label:
      'This visit is focused on evaluating therapies that may support longevity and overall health optimization. These treatments—including peptides and other anti-aging medications—are intended to support normal physiologic processes such as metabolism, energy production, recovery, hormone signaling, cognitive function, immune health, and cellular resilience.\n\nMany of these therapies are not FDA-approved specifically for longevity or anti-aging purposes and are often used off-label. Their long-term safety and effectiveness for these goals may not be fully established. Your provider will review your medical history, medications, symptoms, and personal goals to determine whether these therapies are appropriate for you and to help guide a safe, individualized plan.',
    field: 'peptide_interest',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'get_started', value: "Let's get started" },
        { key: 'not_interested', value: "I'm not interested" }
      ]
    }
  },
  {
    label: 'What was your sex assigned at birth?',
    field: 'peptide_sexAssignedAtBirth',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    }
  },
  {
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    field: 'peptide_pregnancy',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Consent (pregnancy)',
    field: 'peptide_pregnancyConsent',
    type: 'select',
    educationalSlide: `I understand that the supplement prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus. I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value: 'I have read the information and do NOT wish to proceed'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed'
        }
      ]
    }
  },
  {
    label: 'What is your height in feet and inches?',
    field: 'peptide_height',
    type: 'info',
    answer: {
      name: 'peptide_height',
      type: 'text',
      placeholder: 'Feet and inches'
    }
  },
  {
    label: 'What is your weight in pounds?',
    field: 'peptide_weight',
    type: 'info',
    answer: {
      name: 'peptide_weight',
      type: 'number',
      placeholder: 'Pounds'
    }
  },
  {
    label: 'What are you looking to achieve with peptide therapy? (Select all that apply)',
    field: 'peptide_therapyGoals',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'support_sleep', value: 'Support sleep quality' },
        {
          key: 'support_wound_healing',
          value: 'Support wound healing or tissue repair'
        },
        {
          key: 'reduce_intestinal_inflammation',
          value: 'Reduce intestinal inflammation'
        },
        { key: 'reduce_overall_inflammation', value: 'Reduce overall inflammation' },
        {
          key: 'improve_metabolism',
          value: 'Improve metabolism and lose weight'
        },
        {
          key: 'support_brain_cognitive',
          value: 'Support brain/cognitive function or focus'
        },
        {
          key: 'support_skin_quality',
          value: 'Support skin quality or anti-aging support'
        }
      ]
    }
  },
  {
    label: 'Are you currently experiencing any of the following symptoms? (Select all that apply)',
    field: 'peptide_symptoms',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'fatigue', value: 'Fatigue' },
        { key: 'reduced_exercise_recovery', value: 'Reduced exercise recovery' },
        { key: 'brain_fog', value: 'Brain fog' },
        {
          key: 'memory_concentration_difficulties',
          value: 'Memory or concentration difficulties'
        },
        { key: 'anxiety_chronic_stress', value: 'Anxiety or chronic stress' },
        { key: 'slow_wound_healing', value: 'Slow wound healing' },
        { key: 'frequent_infections', value: 'Frequent infections' },
        { key: 'low_libido', value: 'Low libido' },
        { key: 'difficulty_gaining_muscle', value: 'Difficulty gaining muscle' },
        { key: 'difficulty_losing_fat', value: 'Difficulty losing fat' },
        { key: 'sleep_disturbances', value: 'Sleep disturbances' },
        {
          key: 'none_of_the_above',
          value: 'None of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label:
      'Do you currently have or have you ever been diagnosed with any of the following conditions? (Select all that apply)',
    field: 'peptide_conditions',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'active_cancer', value: 'Active cancer' },
        {
          key: 'immune_suppression',
          value:
            'Immune suppression from medications such as Tacrolimus, cyclosporine, methotrexate or biologics use'
        },
        {
          key: 'hypopituitarism',
          value:
            'Hypopituitarism, pituitary tumor or previous pituitary surgery'
        },
        { key: 'acromegaly', value: 'Acromegaly' },
        {
          key: 'systemic_corticosteroids',
          value:
            'A condition requiring continued use of systemic corticosteroids such as prednisone or dexamethasone'
        },
        { key: 'uncontrolled_thyroid', value: 'Uncontrolled thyroid disease' },
        { key: 'uncontrolled_diabetes', value: 'Uncontrolled diabetes' },
        { key: 'insulin_dependent_diabetes', value: 'Insulin dependent diabetes' },
        { key: 'severe_cardiovascular', value: 'Severe cardiovascular disease' },
        {
          key: 'stroke_tia',
          value: 'History of stroke or transient ischemic attack (TIA)'
        },
        {
          key: 'liver_cirrhosis',
          value: 'Liver enzymes elevation or liver cirrhosis'
        },
        {
          key: 'ckd_3b_or_greater',
          value: 'Chronic kidney disease (CKD Stage 3b or greater)'
        },
        {
          key: 'active_infection',
          value: 'Active infection requiring medical treatment'
        },
        { key: 'severe_psychiatric', value: 'Severe psychiatric disorder' },
        {
          key: 'blood_thinners',
          value:
            'Take blood thinners (excluding aspirin) such as coumadin/warfarin, Eliquis, Pradaxa, etc'
        },
        {
          key: 'none_of_the_above',
          value: 'None of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label:
      'Are you currently taking or have recently completed a peptide regimen?',
    field: 'peptide_regimen',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label:
      'What products were you taking, did they work well, or did you experience side effects?',
    field: 'peptide_regimenDetails',
    type: 'info',
    answer: {
      name: 'peptide_regimenDetails',
      type: 'text',
      rows: 4,
      placeholder: 'Products, how well they worked, and any side effects'
    }
  },
  {
    label: 'What other information or questions do you have for the doctor?',
    field: 'peptide_otherInformation',
    type: 'info',
    answer: {
      name: 'peptide_otherInformation',
      type: 'text',
      rows: 4,
      placeholder: 'Optional details or questions'
    }
  },
  {
    label: 'Consent (Peptide Therapy)',
    field: 'peptide_treatmentConsent',
    type: 'select',
    educationalSlide: `You are requesting evaluation and possible treatment with peptide therapy. Peptides are short chains of amino acids that act as signaling molecules in the body and may influence processes such as immune function, hormone signaling, metabolism, tissue repair, neurological function, or reproductive hormones depending on the specific peptide used. Examples of peptides that may be prescribed include compounds that influence the growth hormone axis, immune modulation, tissue repair, cognitive function, or skin health.

Many peptide therapies used in wellness, metabolic, or anti-aging programs are not approved by the U.S. Food and Drug Administration (FDA) for these specific uses. When prescribed, they are often used off-label and may be obtained through licensed compounding pharmacies that prepare medications for individual patients. Compounded medications are not evaluated or approved by the FDA for safety, effectiveness, or quality in the same way as commercially manufactured medications.

Potential benefits of peptide therapy may include improvement in areas such as recovery, immune function, metabolic support, cognitive function, tissue repair, hormone signaling, or general wellness depending on the peptide selected. However, clinical evidence may be limited or evolving, and results may vary between individuals.

Potential risks and side effects may include, but are not limited to:
- Injection site reactions such as pain, redness, or swelling
- Headache
- Fatigue or dizziness
- Nausea
- Changes in appetite
- Fluid retention or swelling
- Changes in blood sugar levels
- Hormonal changes depending on the peptide used
- Allergic reactions or unexpected side effects

Because many peptides influence hormone pathways, immune activity, or neurological signaling, long-term safety data for some therapies may be limited.

Some peptide therapies may require subcutaneous injection or intranasal administration, and patients must follow all instructions for safe use and storage.

By proceeding with this visit, you acknowledge and understand that:
- Peptide therapy may be prescribed off-label.
- Many peptide products are not FDA-approved for the specific indication being treated.
- Compounded medications may be used and are not FDA-approved in the same way as commercially manufactured drugs.
- The long-term safety and effectiveness of some peptides may not be fully established.
- Treatment decisions are based on the clinical judgment of your healthcare provider and the information you provide during this visit.

You agree to:
- Provide accurate and complete medical information.
- Inform your provider of any new medical conditions, medications, or side effects.
- Follow prescribed dosing instructions.
- Seek medical attention if concerning symptoms occur.

This visit is intended to evaluate whether peptide therapy may be appropriate for you. Your provider may recommend treatment, request additional information, suggest alternative therapies, or determine that peptide therapy is not appropriate or safe based on your medical history.

By proceeding with this visit, you confirm that you understand the information above and consent to evaluation and possible treatment with peptide therapy under the supervision of a licensed healthcare provider.

I verify that I am the patient and that I have answered the questions asked in this intake form.  I confirm that I have reviewed and understood all the questions asked of me.  I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor.  
`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value:
            'I have read the above information and I do not wish to continue'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        }
      ]
    }
  },
  {
    field: 'peptide_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'peptide_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    reject: true,
    isLast: true,
    answer: {},
    extras: { sendToBeluga: false }
  },
  {
    class: 'ineligible',
    field: 'peptide_didNotConsent',
    label: 'We need your consent to continue',
    subLabel: 'You did not agree to the terms shown on the previous step. Without that consent, we cannot move forward with your visit or prescribe treatment.',
    extras: { sendToBeluga: false },
    answer: {},
    type: 'info',
    isLast: true,
    reject: true,
  },
] as const;

export type PeptideQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof peptideQuestions>
> &
  GenericHeightWeightQuestionFieldAnswers;
export default peptideQuestions;
